import type { ForwardedRef, MouseEventHandler } from 'react';
import type { CounterProps, CounterRef } from '@ricoh/ria.client.core/components/indicator/counter';

import { useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doNothing } from '@ricoh/ria.client.core/constants/core/action';
import { useUpdateEffect } from '@ricoh/ria.client.core/hooks/app/lifecycle';
import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { useCoreStoreSelector } from '@ricoh/ria.client.core/plugins/store/hooks/selector';
import { isNull } from '@ricoh/ria.client.core/utils/data/object';

import type { DialogSessionHooks, DialogSessionProps, DialogSessionRef } from './_.type';

/**
 * Use DialogSession hooks.
 *
 * @param {DialogSessionProps} props DialogSession props.
 * @param {DialogSessionRef} ref DialogSession ref.
 *
 * @returns {DialogSessionHooks} DialogSession hooks.
 */
export default function useDialogSessionHooks(
  { sessionDuration, counterInitialValue, onExtend, onRevoke }: DialogSessionProps,
  ref: ForwardedRef<DialogSessionRef>,
): DialogSessionHooks {
  const counterRef = useRef<CounterRef>(null);

  const { t } = useTranslation();

  const startAt = useCoreStoreSelector((state) => state.component_dialog.session.startAt);

  const [show, setShow] = useState<boolean>(false);

  const renderCounter = useCallback<CounterProps['render']>(
    (value) => {
      if (isNull(value)) {
        return null;
      }

      const duration = DateTime.duration(value, 'second');
      return t(`session.message`, {
        duration: duration.format('mm:ss', {
          trim: false,
        }),
      });
    },
    [t],
  );

  const onClickExtend = useCallback<MouseEventHandler>(() => {
    onExtend();

    setShow(() => false);
  }, [onExtend]);

  const onClickRevoke = useCallback<MouseEventHandler>(() => {
    onRevoke();

    setShow(() => false);
  }, [onRevoke]);

  useLayoutEffect(() => {
    if (isNull(startAt)) {
      return doNothing;
    }

    const timeout = setTimeout(() => {
      setShow(() => true);
    }, sessionDuration);

    return () => {
      clearTimeout(timeout);
    };
  }, [sessionDuration, startAt]);

  useUpdateEffect(() => {
    const counter = counterRef.current;
    if (!counter) {
      return;
    }

    if (show) {
      counter.count({
        initialValue: counterInitialValue,
        update: (value: number) => value - 1,
        predicate: (value: number) => value > 0,
        onDone: onRevoke,
      });
    } else {
      counter.cancel();
    }

    // * Just watch `show` for start and stop counter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  useImperativeHandle(ref, () => ({}));

  return useMemo<DialogSessionHooks>(
    () => ({
      refs: {
        counterRef,
      },

      states: {
        showState: [show, setShow],

        renderCounter,
      },

      events: {
        onClickExtend,
        onClickRevoke,
      },
    }),
    [onClickExtend, onClickRevoke, renderCounter, setShow, show],
  );
}
